import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { notifications as api } from "@/lib/api/notifications";
import type { NotificationActionKind, NotificationItem } from "@/types/api";
import { useAuth } from "./useAuth";

export type NotificationsStatus = "idle" | "loading" | "ready" | "error";

export interface NotificationsContextValue {
  status: NotificationsStatus;
  notifications: readonly NotificationItem[];
  unreadCount: number;
  error: Error | null;
  reload: (params?: { archived?: boolean }) => Promise<void>;
  markRead: (id: string) => Promise<NotificationItem>;
  archive: (id: string) => Promise<NotificationItem>;
  remove: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
}

const NotificationsContext = createContext<NotificationsContextValue>(null!);

export function useNotifications(): NotificationsContextValue {
  return useContext(NotificationsContext);
}

function useNotificationsProvider(): NotificationsContextValue {
  const { status: authStatus } = useAuth();
  const [status, setStatus] = useState<NotificationsStatus>("idle");
  const [notifications, setNotifications] = useState<
    readonly NotificationItem[]
  >([]);
  const [error, setError] = useState<Error | null>(null);

  const reload = useCallback(
    (params?: { archived?: boolean }) =>
      api
        .listNotifications(params)
        .then(({ notifications }) => {
          setNotifications(notifications);
          setStatus("ready");
          setError(null);
        })
        .catch((e: Error) => {
          setError(e);
          setStatus("error");
        }),
    [],
  );

  useEffect(() => {
    if (authStatus !== "authenticated") return;
    void reload();
  }, [authStatus, reload]);

  const update = useCallback(
    async (
      id: string,
      action: NotificationActionKind,
    ): Promise<NotificationItem> => {
      const { notification } = await api.updateNotification(id, action);
      setNotifications((prev) =>
        action === "archived"
          ? prev.filter((n) => n.id !== id) // archived rows leave the default view
          : prev.map((n) => (n.id === id ? notification : n)),
      );
      return notification;
    },
    [],
  );

  const markRead = useCallback((id: string) => update(id, "read"), [update]);
  const archive = useCallback((id: string) => update(id, "archived"), [update]);

  const remove = useCallback(async (id: string) => {
    await api.deleteNotification(id);
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const markAllRead = useCallback(async () => {
    await api.markAllNotificationsRead();
    const now = new Date().toISOString();
    setNotifications((prev) =>
      prev.map((n) => (n.read_at ? n : { ...n, read_at: now })),
    );
  }, []);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.read_at && !n.archived_at).length,
    [notifications],
  );

  return useMemo(
    () => ({
      status,
      notifications,
      unreadCount,
      error,
      reload,
      markRead,
      archive,
      remove,
      markAllRead,
    }),
    [
      status,
      notifications,
      unreadCount,
      error,
      reload,
      markRead,
      archive,
      remove,
      markAllRead,
    ],
  );
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const value = useNotificationsProvider();
  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
